import { GetStaticProps } from 'next';
import Cart from '@screens/Cart';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { prismicService } from '@services/businessContainer';
import {
  IPrismicCustomTypeContent,
  SlicesContainer,
} from '@screens/SlicesContainer';
import { IPrismicCartSharedContents } from '@monofront/types';
import { useTranslations } from 'next-intl';
import ModalFreeTrialStarted from '@components/ModalFreeTrialStarted';
import { useModalFreetrialStarted } from '@components/ModalFreeTrialStarted/ModalFreeTrialStarted';
import { LFPage, injectLayoutData } from '@utils/layout';
import PaypalProvider from '@context/PaypalProvider/PaypalProvider';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

interface IPageCartProps {
  pageContents?: IPrismicCustomTypeContent[];
  headerContent?: IPrismicCustomTypeContent[];
  prismicCartSharedContents?: IPrismicCartSharedContents;
}

const PageCart: LFPage<IPageCartProps> = ({
  pageContents,
  headerContent,
  prismicCartSharedContents,
}) => {
  const router = useRouter();
  const { isModalOpen, closeModal: closeFreeTrialModal } =
    useModalFreetrialStarted();

  const tCart = useTranslations('Cart');

  const urlPath = router.asPath.split('?')[0];

  return (
    <>
      <NextSeo
        title={tCart('yourCart')}
        canonical={`${appBaseUrl}${urlPath}`}
        openGraph={{
          url: `${appBaseUrl}${urlPath}`,
          title: tCart('yourCart'),
        }}
      />
      <PaypalProvider namespace="cart" options={{ components: 'messages' }}>
        <Cart
          headerRender={
            headerContent ? (
              <SlicesContainer pageContents={headerContent} />
            ) : null
          }
          prismicCartSharedContents={prismicCartSharedContents!}
        >
          {pageContents ? (
            <SlicesContainer pageContents={pageContents} />
          ) : null}
        </Cart>
      </PaypalProvider>
      <ModalFreeTrialStarted
        isOpen={isModalOpen}
        closeModal={closeFreeTrialModal}
        buttonText={tCart('freeTrialStartedModalBackToCart')}
      />
    </>
  );
};

export const getStaticProps = injectLayoutData<IPageCartProps, GetStaticProps>(
  async ({ previewData, locale }) => {
    const {
      results: [document],
    } = await prismicService.getDocumentsByCustomTypeName(
      'shopcart',
      previewData,
      {
        fetchLinks: 'cartsharedcontents.body',
        ...(locale ? { lang: `${locale}-${locale}` } : {}),
      },
    );

    return {
      props: {
        headerContent: document.data.body2,
        pageContents: document.data.body,
        prismicCartSharedContents:
          document.data.cart_shared_contents?.data?.body?.[0] || null,
      },
      revalidate: 1800, // 30min
    };
  },
);

export default PageCart;
